import LazyPhotoManagerUploadDialog from '@/components/photo-manager/LazyPhotoManagerUploadDialog'
import type { MediaUploadUsageValue } from '@/lib/enums/MediaUploadUsage'
import { DEFAULT_UPLOAD_USAGE } from '@/lib/media-upload-profiles'
import { type AssetSelectionCounts, getAssetMedia } from '@/lib/media-editing'
import {
  getPhotoManagerConfig,
  type PhotoManagerDefaultUploadDestinationValue,
  type PhotoManagerModeValue,
  type PhotoManagerSelectionModeValue,
} from '@/lib/photo-manager-config'
import type { MediaFile } from '@/lib/services/media/media.types'
import type { PhotoAssetWithMedia } from '@/lib/services/photo-assets/photo-assets.types'
import type { Body, Meta, UppyEventMap, UppyFile } from '@uppy/core'

interface ImageUploaderProps {
  id?: string
  /** Gets each saved upload, and each photo manager pick unless `onSelectAssets` takes them. */
  onUploadSuccess?: (
    file: UppyFile<Meta, Body> | { source: 'photo-manager-selection' } | undefined,
    response: { body?: MediaFile }
  ) => void
  onUploadError?: UppyEventMap<Meta, Body>['upload-error']
  onUploadComplete?: UppyEventMap<Meta, Body>['complete']
  onSelectAssets?: ((assets: PhotoAssetWithMedia[]) => AssetSelectionCounts) | null
  uploadUsage?: MediaUploadUsageValue
  photoManagerMode?: PhotoManagerModeValue
  defaultUploadDestination?: PhotoManagerDefaultUploadDestinationValue
  selectionMode?: PhotoManagerSelectionModeValue
  maxNumberOfFiles?: number
  open: boolean
  onRequestClose: () => void
}

const ImageUploader = ({
  id,
  onUploadSuccess = () => {},
  onUploadError = () => {},
  onUploadComplete = () => {},
  onSelectAssets = null,
  uploadUsage = DEFAULT_UPLOAD_USAGE,
  photoManagerMode,
  defaultUploadDestination,
  selectionMode,
  maxNumberOfFiles,
  open,
  onRequestClose,
}: ImageUploaderProps) => {
  const photoManagerConfig = getPhotoManagerConfig({ mode: photoManagerMode, uploadUsage })

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      onRequestClose?.()
    }
  }

  const handleSelectAssets = (assets: PhotoAssetWithMedia[] = []) => {
    if (onSelectAssets) {
      return onSelectAssets(assets)
    }

    let insertedCount = 0
    let skippedCount = 0

    assets.forEach((asset) => {
      const media = getAssetMedia(asset)

      if (!media?.id || !media?.url) {
        skippedCount += 1
        return
      }

      insertedCount += 1
      onUploadSuccess({ source: 'photo-manager-selection' }, { body: media })
    })

    return { insertedCount, skippedCount }
  }

  if (!open) {
    return null
  }

  return (
    <LazyPhotoManagerUploadDialog
      id={id}
      open={open}
      onOpenChange={handleOpenChange}
      onUploadSuccess={onUploadSuccess}
      onUploadError={onUploadError}
      onUploadComplete={onUploadComplete}
      onSelectAssets={handleSelectAssets}
      uploadUsage={uploadUsage}
      photoManagerMode={photoManagerConfig.mode}
      defaultUploadDestination={defaultUploadDestination}
      selectionMode={selectionMode}
      maxNumberOfFiles={maxNumberOfFiles}
    />
  )
}

export default ImageUploader
